import { SearchOutlined } from '@ant-design/icons';
import { Input, Select, Typography, List, Tag, Tooltip } from 'antd';
import {
  useGetAllResourceTypesQuery,
  useGetUsedResourcesByResourceTypeQuery,
} from 'app/api';
import CustomListItem from 'app/components/CustomListItem/CustomListItem';
import { ItemsList } from 'app/components/LayoutComponents/LayoutComponents';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toFixedIfFloat } from 'utils/helpers';
import * as S from './UsedResourcesByTypeCard.styles';

interface IProps {
  setSelectedUsedResourceId: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;

  selectedUsedResourceId: number | undefined;
}

const UsedResourcesByTypeCard: FC<IProps> = ({
  selectedUsedResourceId,
  setSelectedUsedResourceId,
}) => {
  const { t } = useTranslation();

  const envId = useSelector(selectSelectedEnvId);
  const companyId = useSelector(selectSelectedCompanyId);
  const seasonId = useSelector(selectSelectedSeasonId);

  const [searchQuery, setSearchQuery] = useState('');
  const [resourceTypeId, setResourceTypeId] = useState<number | undefined>();

  const {
    data: resourceTypes,
    isFetching: isResourceTypesLoading,
  } = useGetAllResourceTypesQuery(undefined);

  const {
    data: usedResources,
    isFetching,
  } = useGetUsedResourcesByResourceTypeQuery(
    {
      envId: envId as number,
      companyId: companyId || undefined,
      seasonId: seasonId as number,
      resourceTypeId,
      textSearch: searchQuery.trim(),
    },
    { skip: !envId || !seasonId },
  );
  const getTooltipText = (allPricesPresent: boolean | null) => {
    if (allPricesPresent === false) {
      return t(
        'Resource.UsedResource.MetaTooltip.NotAllCompaniesPrices',
        'Not all companies have a price',
      );
    } else if (allPricesPresent === null) {
      return t(
        'Resource.UsedResource.MetaTooltip.SpecifyPrice',
        'Please specify a price',
      );
    }
    return t(
      'Resource.UsedResource.MetaTooltip.AllCompaniesPrices',
      'All companies have a price',
    );
  };
  return (
    <ItemsList.Card bordered={false}>
      <div
        style={{
          margin: '0px 24px 9px',
          width: 'calc(100% - 48px)',
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={e => setSearchQuery(e.currentTarget.value)}
          placeholder={t('Field.List.SearchPlaceholder', 'Search')}
        />
        <Select
          style={{
            margin: '20px 0',
            width: '100%',
          }}
          placeholder={t(
            'Resource.UsedResource.SelectResourceType',
            'Resource type',
          )}
          onChange={value => setResourceTypeId(value)}
          value={resourceTypeId}
          allowClear
          loading={isResourceTypesLoading}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {resourceTypes?.map(resourceType => (
            <Select.Option key={resourceType.id} value={resourceType.id}>
              {resourceType.name}
            </Select.Option>
          ))}
        </Select>
        <Typography.Text>
          {t('Common.Displayed', 'Displayed: ')}
          {usedResources?.length ?? ''}
        </Typography.Text>
      </div>
      <List
        loading={isFetching}
        itemLayout="horizontal"
        dataSource={usedResources}
        renderItem={usedResource => {
          const {
            resource: { id, cultureVariation, name },
            resourceType,
            totalAmount,
            priceStatus: { averagePrice, pricesCount, allPricesPresent },
          } = usedResource;

          const isActive = selectedUsedResourceId === id;

          return (
            <CustomListItem
              key={id}
              isActive={isActive}
              onClick={() =>
                setSelectedUsedResourceId(usedResource.resource.id)
              }
              actions={[
                <Tag
                  key="0"
                  color={totalAmount >= 0 ? 'green' : 'red'}
                >{`${toFixedIfFloat(totalAmount)} ${resourceType?.unit}`}</Tag>,
              ]}
            >
              <List.Item.Meta
                title={cultureVariation?.culture.name || name}
                description={
                  <S.Meta.Container>
                    <S.Meta.TypeText
                      title={cultureVariation?.name || resourceType?.name}
                    >
                      {cultureVariation?.name || resourceType?.name}
                    </S.Meta.TypeText>
                    <Tooltip
                      placement="right"
                      title={getTooltipText(allPricesPresent)}
                    >
                      <S.Meta.Price>
                        {`${`${
                          pricesCount && averagePrice !== null
                            ? toFixedIfFloat(averagePrice)
                            : '-'
                        } €/${resourceType.unit}`}`}
                        <S.Meta.StatusCircleIcon
                          $allPricesPresent={allPricesPresent}
                        />
                      </S.Meta.Price>
                    </Tooltip>
                  </S.Meta.Container>
                }
              />
            </CustomListItem>
          );
        }}
      />
    </ItemsList.Card>
  );
};

export default UsedResourcesByTypeCard;
